/**
 * audioBus — Premium showroom audio engine (calibrated luxury edition).
 *
 * Sonic identity: a quiet architectural lighting showroom. Soft glass glints,
 * felted metal taps, dimmer-like detents, gallery air. Never arcade, never
 * sci-fi, never a notification chime. Audible — not buried.
 *
 * Calibration goal: every important interaction must be CLEARLY audible on
 * MacBook speakers at full volume AND on iPhone speakers at full volume,
 * while remaining tasteful on headphones. Luxury is not silence.
 *
 * Design rules
 * ------------
 *   • All cues are procedural Web Audio: filtered pink-noise foley plus
 *     very short low-mid sine kisses, routed through a warm low-pass and
 *     a master soft limiter (DynamicsCompressor) for safety.
 *   • Cue peaks per spec (premiumTap 0.035–0.070, wheelDetent 0.030–0.060,
 *     productOpen 0.040–0.080, etc.) — strong enough to actually hear.
 *   • No square/sawtooth waves. No hard pitch sweeps. No detuned chord stacks.
 *   • Hover sounds are desktop-only and opt-in via data-sound="hover".
 *   • Tap sounds delegate to <button>/[role="button"] and any [data-sound].
 *   • Global 110ms throttle + per-cue cooldown; cannot be spammed.
 *   • Sound is OFF until first real user gesture; respects prefers-reduced-motion
 *     for the transition cue and the ambient layer.
 *   • Ambient layer is filtered pink noise — never a melody or rhythm — fades
 *     in slowly, fades out on tab hidden / blur / mute / route change.
 *
 * Storage keys (mirrored, default ON):
 *   fp.sound.enabled         "1" | "0"
 *   frankPistoriusSound      "on" | "off"   (legacy / tactileFeedback)
 *
 * Site-wide bindings: any element with `data-sound="<cue>"` will emit that
 * cue on tap. Elements with `data-sound="off"` are excluded. Hover-eligible
 * elements opt in with `data-sound="hover"` (desktop only).
 */

type SoundName =
  | "hover"
  | "nav-hover"
  | "tap"
  | "click"
  | "open"
  | "close"
  | "transition"
  | "reveal"
  | "chime"
  | "success"
  | "error";

export type SoundMode = "off" | "subtle" | "immersive";
export type SoundScene =
  | "home-showroom"
  | "products-gallery"
  | "product-focus"
  | "manufacturer-kdln"
  | "manufacturer-quasar"
  | "manufacturer-vistosi"
  | "manufacturer-nemo"
  | "manufacturer-a-emotional"
  | "manufacturer-vibieffe"
  | "contact-silent"
  | "neutral";

const STORAGE_KEY = "fp.sound.enabled";
const LEGACY_KEY  = "frankPistoriusSound";
const MODE_KEY    = "fp.sound.mode";
// Master output gain — calibrated against MacBook + iPhone speakers.
// A soft DynamicsCompressor sits after master to catch overlap peaks.
// Pushed up so cues remain clearly audible at 50–70% device volume.
const MASTER_GAIN = 1.10;
// Per-mode perceived-loudness scaler applied to every cue voice.
//   tactile  — clearly audible UI level on phone/laptop speakers
//   showroom — slightly richer/present, NOT dramatically louder
const MODE_GAIN: Record<SoundMode, number> = { off: 0, subtle: 0.95, immersive: 1.15 };
const AMBIENT_GAIN = 0.006; // ambient stays near-invisible

const isBrowser = typeof window !== "undefined";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let masterFilter: BiquadFilterNode | null = null;
let masterLimiter: DynamicsCompressorNode | null = null;
// Live cursor in viewport-normalized [0..1]. Used so EVERY cue is spatialized
// (X → stereo pan, Y → brightness/filter cutoff). Defaults to centre so cues
// triggered by keyboard or before any pointer move still feel intentional.
let cursorX = 0.5;
let cursorY = 0.5;
// Per-call routing target: noise/sine voices connect here instead of `master`
// so play() can splice a per-cue spatial chain (panner + Y-filter) in front.
let voiceTarget: AudioNode | null = null;
function dest(): AudioNode | null { return voiceTarget ?? master; }
let pinkBuffer: AudioBuffer | null = null;
let ambient: { src: AudioBufferSourceNode; gain: GainNode; filter: BiquadFilterNode } | null = null;
const lastPlayed = new Map<SoundName, number>();
let lastAnyPlay = 0;
const listeners = new Set<(enabled: boolean) => void>();

function reducedMotion(): boolean {
  if (!isBrowser) return false;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
}

function isFinePointer(): boolean {
  if (!isBrowser) return false;
  try { return window.matchMedia("(hover: hover) and (pointer: fine)").matches; }
  catch { return false; }
}

function readEnabled(): boolean {
  if (!isBrowser) return false;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy === "on") return true;
    if (legacy === "off") return false;
    // First visit — sound is OFF until the user explicitly chooses
    // Tactile or Showroom from the SoundToggle. Browsers + accessibility
    // require this; we never autoplay on a fresh session.
    return false;
  } catch { return false; }
}

function writeEnabled(on: boolean) {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    window.localStorage.setItem(LEGACY_KEY, on ? "on" : "off");
  } catch { /* */ }
}

function readMode(): SoundMode {
  if (!isBrowser) return "off";
  try {
    const v = window.localStorage.getItem(MODE_KEY) as SoundMode | null;
    if (v === "off" || v === "subtle" || v === "immersive") return v;
  } catch { /* */ }
  // First visit defaults to Off — see readEnabled() above.
  return "off";
}

function writeMode(m: SoundMode) {
  if (!isBrowser) return;
  try { window.localStorage.setItem(MODE_KEY, m); } catch { /* */ }
}

function ensureCtx(): AudioContext | null {
  if (!isBrowser) return null;
  if (ctx) return ctx;
  const Ctor = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
  if (!Ctor) return null;
  ctx = new Ctor();
  master = ctx.createGain();
  master.gain.value = MASTER_GAIN;
  // Warm gallery low-pass — opened up so cues remain clearly audible on
  // laptop & phone speakers, but high frequencies stay rolled-off & elegant.
  masterFilter = ctx.createBiquadFilter();
  masterFilter.type = "lowpass";
  masterFilter.frequency.value = 9000;
  masterFilter.Q.value = 0.5;
  // Soft limiter — protects against overlap peaks / clipping without
  // pumping. Acts as a gentle ceiling around -3 dBFS.
  // Soft limiter — protects against overlap peaks / clipping without
  // pumping. Acts as a gentle ceiling around -4 dBFS now that master is
  // pushed for laptop/phone audibility.
  masterLimiter = ctx.createDynamicsCompressor();
  masterLimiter.threshold.value = -8;
  masterLimiter.knee.value = 8;
  masterLimiter.ratio.value = 8;
  masterLimiter.attack.value = 0.003;
  masterLimiter.release.value = 0.18;
  master.connect(masterFilter).connect(masterLimiter).connect(ctx.destination);
  return ctx;
}

/** Per-cue gain scalar driven by the active sound mode. */
function modeScale(): number { return MODE_GAIN[readMode()] ?? 1.0; }

/* ── Procedural building blocks ──────────────────────────────────────── */

function makePinkBuffer(c: AudioContext, seconds = 4): AudioBuffer {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  // Voss-McCartney pink noise approximation
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.96900 * b2 + white * 0.1538520;
    b3 = 0.86650 * b3 + white * 0.3104856;
    b4 = 0.55000 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.0168980;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  return buf;
}

function ensurePink(): AudioBuffer | null {
  const c = ensureCtx();
  if (!c) return null;
  if (!pinkBuffer) pinkBuffer = makePinkBuffer(c);
  return pinkBuffer;
}

/** Short noise burst, lowpass + highpass shaped, gentle envelope. */
function noiseFoley(opts: {
  duration: number;
  peak: number;
  lp: number;
  hp?: number;
  attack?: number;
  lpSweepTo?: number;
}) {
  const c = ensureCtx();
  if (!c || !master) return;
  const out = dest();
  if (!out) return;
  const buf = ensurePink();
  if (!buf) return;
  const t = c.currentTime;
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  // Random offset so successive bursts don't sound identical.
  src.loopStart = 0;
  src.loopEnd = buf.duration;
  const offset = Math.random() * (buf.duration - opts.duration - 0.1);

  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(opts.lp, t);
  lp.Q.value = 0.4;
  if (opts.lpSweepTo) {
    lp.frequency.exponentialRampToValueAtTime(opts.lpSweepTo, t + opts.duration);
  }

  const hp = c.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = opts.hp ?? 180;
  hp.Q.value = 0.5;

  const g = c.createGain();
  const attack = Math.min(opts.attack ?? 0.018, opts.duration * 0.4);
  const peak = Math.max(0.0002, opts.peak * modeScale());
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + opts.duration);

  src.connect(hp).connect(lp).connect(g).connect(out);
  src.start(t, offset);
  src.stop(t + opts.duration + 0.05);
  src.onended = () => { try { src.disconnect(); g.disconnect(); lp.disconnect(); hp.disconnect(); } catch {} };
}

/** Soft sine partial — single voice, gentle exp envelope. */
function softSine(opts: { freq: number; duration: number; peak: number; attack?: number; sweepTo?: number; type?: OscillatorType }) {
  const c = ensureCtx();
  if (!c || !master) return;
  const out = dest();
  if (!out) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  osc.type = opts.type ?? "sine";
  osc.frequency.setValueAtTime(opts.freq, t);
  if (opts.sweepTo) osc.frequency.exponentialRampToValueAtTime(opts.sweepTo, t + opts.duration);
  const g = c.createGain();
  const attack = Math.min(opts.attack ?? 0.012, opts.duration * 0.4);
  const peak = Math.max(0.0002, opts.peak * modeScale());
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + opts.duration);
  osc.connect(g).connect(out);
  osc.start(t);
  osc.stop(t + opts.duration + 0.04);
  osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch {} };
}

/**
 * withSpatial — splice a per-cue spatial chain (StereoPanner + Y-driven
 * lowpass) in front of `master` for the duration of `fn()`. Subtle stereo
 * placement only — pan stays in ±0.35 (hard cap ±0.45). This is intentional
 * geometry, NOT a spatial-audio demo. Y maps to filter cutoff for warmth.
 */
function withSpatial(x: number, y: number, fn: () => void) {
  const c = ensureCtx();
  if (!c || !master) { fn(); return; }
  const xn = Math.max(0, Math.min(1, x));
  const yn = Math.max(0, Math.min(1, y));
  const pan = c.createStereoPanner();
  // Subtle pan: ±0.35 typical, hard-capped at ±0.45.
  pan.pan.value = Math.max(-0.45, Math.min(0.45, (xn - 0.5) * 0.7));
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 2400 + (1 - yn) * 6600; // 2400Hz (warm/bottom) → 9000Hz (top)
  lp.Q.value = 0.5;
  // Very small presence delta — geometry, not loudness theatrics.
  const g = c.createGain();
  g.gain.value = 0.96 + (1 - yn) * 0.06;
  pan.connect(lp).connect(g).connect(master);
  const prev = voiceTarget;
  voiceTarget = pan;
  try { fn(); } finally { voiceTarget = prev; }
  // Tear down the spatial chain after the longest possible recipe tail.
  setTimeout(() => { try { g.disconnect(); lp.disconnect(); pan.disconnect(); } catch {} }, 900);
}

/* ── Premium recipes — material foley only ──────────────────────────────
 * Hard rules enforced here:
 *   • No naked sine/triangle as the main sound. Any oscillator support is
 *     ≤20ms, well below the noise body level, and heavily filtered.
 *   • No melodic intervals, no pitch sweeps, no chimes.
 *   • Everything sounds like a physical material event, not a note.
 */

const premiumHover = () => {
  // Airy glass glint — quiet but actually audible on laptop speakers.
  noiseFoley({ duration: 0.05, peak: 0.030, lp: 8800, hp: 5400, attack: 0.006 });
};

const premiumTap = () => {
  // Felted metal switch: filtered contact noise + ultra-short low-mid kiss.
  noiseFoley({ duration: 0.07, peak: 0.060, lp: 2200, hp: 320, attack: 0.003 });
  softSine({ freq: 240, duration: 0.018, peak: 0.024, attack: 0.002 });
};

const premiumRoute = () => {
  if (reducedMotion()) return;
  // Quiet gallery curtain: slow filtered air sweep, pure noise.
  noiseFoley({ duration: 0.24, peak: 0.045, lp: 2400, hp: 220, attack: 0.06, lpSweepTo: 700 });
};

const premiumLightboxOpen = () => {
  // Glass-room reveal: warm filtered air + a very short bright glass edge.
  noiseFoley({ duration: 0.32, peak: 0.075, lp: 3400, hp: 320, attack: 0.07, lpSweepTo: 1100 });
  noiseFoley({ duration: 0.20, peak: 0.038, lp: 7600, hp: 4200, attack: 0.04 });
};

const premiumLightboxClose = () => {
  // Felt-lined cabinet: damped downward air, no body tone.
  noiseFoley({ duration: 0.20, peak: 0.055, lp: 1500, hp: 220, attack: 0.05, lpSweepTo: 480 });
};

const premiumDimmer = () => {
  // Two-stage dimmer detent — material clicks, no harmonic.
  noiseFoley({ duration: 0.024, peak: 0.045, lp: 3800, hp: 900, attack: 0.0025 });
  setTimeout(() => noiseFoley({ duration: 0.030, peak: 0.038, lp: 2900, hp: 700, attack: 0.003 }), 80);
};

const premiumSuccess = () => {
  // Confident, understated material rise — pure filtered air, no chime.
  noiseFoley({ duration: 0.28, peak: 0.070, lp: 2700, hp: 360, attack: 0.06, lpSweepTo: 1500 });
  noiseFoley({ duration: 0.20, peak: 0.032, lp: 7200, hp: 4400, attack: 0.05 });
};

const premiumError = () => {
  // Polite low material tap — never alarming, no buzz, no tone.
  noiseFoley({ duration: 0.11, peak: 0.045, lp: 1200, hp: 220, attack: 0.005 });
};

const RECIPES: Record<SoundName, () => void> = {
  "hover":      premiumHover,
  "nav-hover":  premiumHover,
  "tap":        premiumTap,
  "click":      premiumTap,
  "open":       premiumLightboxOpen,
  "close":      premiumLightboxClose,
  "transition": premiumRoute,
  "reveal":     premiumRoute,
  "chime":      premiumDimmer,
  "success":    premiumSuccess,
  "error":      premiumError,
};

const PER_CUE_THROTTLE_MS: Record<SoundName, number> = {
  "hover":      260,
  "nav-hover":  260,
  "tap":        110,
  "click":      110,
  "open":       300,
  "close":      280,
  "transition": 700,
  "reveal":     500,
  "chime":      300,
  "success":    500,
  "error":      400,
};

const GLOBAL_THROTTLE_MS = 110; // luxury restraint — cannot spam

/* ── Wheel cues (Apple-style picker) ─────────────────────────────────── */
// Independent of the global throttle so rapid detent ticks can fire while
// the wheel is being spun. Still respects the global mute toggle and the
// first-gesture unlock.

type WheelCue = "tick" | "select" | "settle" | "start" | "end";
type WheelOpts = { velocity?: number; direction?: "up" | "down" };
const lastWheelAt: Record<WheelCue, number> = { tick: 0, select: 0, settle: 0, start: 0, end: 0 };
// Detent throttle: ~28ms minimum spacing → max ~35 ticks/sec on fast flicks.
const WHEEL_THROTTLE_MS: Record<WheelCue, number> = { tick: 28, select: 80, settle: 120, start: 0, end: 0 };

const wheelRecipes: Record<WheelCue, (opts?: WheelOpts) => void> = {
  // Mechanical dimmer-wheel detent — pure filtered noise transient.
  // Velocity slightly brightens & shortens; direction nudges the spectrum.
  // No oscillator/sine partial — would otherwise read as a tone.
  tick: (opts) => {
    const v = Math.max(0, Math.min(1, opts?.velocity ?? 0.4));
    const dir = opts?.direction ?? "down";
    const dur = 0.016 + (1 - v) * 0.012;          // 16–28ms
    const peak = 0.045 + v * 0.015;                // 0.045–0.060
    const lp = (dir === "up" ? 4800 : 4200) + v * 1800;
    const hp = (dir === "up" ? 1100 :  900) + v * 400;
    noiseFoley({ duration: dur, peak, lp, hp, attack: 0.0018 });
  },
  // Tap-to-select on a wheel item: warm felted lock-in (noise only).
  select: () => {
    noiseFoley({ duration: 0.05, peak: 0.055, lp: 3600, hp: 700, attack: 0.003 });
  },
  // Final settle — softer than detent, warmer band.
  settle: () => {
    noiseFoley({ duration: 0.030, peak: 0.034, lp: 2800, hp: 600, attack: 0.0028 });
  },
  // Drag start/end — intentionally silent; the detents are the sound.
  start: () => { /* no-op: wheel sound = detents only */ },
  end:   () => { /* no-op: wheel sound = detents only */ },
};

/* ── Showroom scene profiles ─────────────────────────────────────────────
 *
 * Each scene paints a different "room tone" character. All are filtered pink
 * noise — never a chord, never a melody — but the filter freq/Q + a tiny
 * shimmer partial give each manufacturer page a subtle sonic identity.
 * Subtle stereo placement only — never a spatial-audio demo.
 */
type SceneProfile = {
  /** Lowpass cutoff (Hz) — defines warmth/darkness of the room tone. */
  lp: number;
  /** Lowpass Q. */
  q: number;
  /** Final ambient gain (peak). 0 = silent scene. */
  gain: number;
  /** Optional sine partial for very subtle resonance (Hz). */
  partialHz?: number;
  /** Partial gain (peak), 0 = none. */
  partialGain?: number;
  /** Stereo width hint, -1..1 stereo pan target (oscillates slowly). */
  pan?: number;
};

const SCENE_PROFILES: Record<SoundScene, SceneProfile> = {
  // Pure filtered pink-noise air. No partials, no tone, no melody.
  // Differences between scenes come ONLY from filter cutoff/Q + gain —
  // perceived as "warmer / cooler / more present / less present" room air.
  // Absolute max gain is 0.006 (per design brief).
  "neutral":               { lp: 520, q: 0.4, gain: 0.0030 },
  "home-showroom":         { lp: 540, q: 0.4, gain: 0.0035 },
  "products-gallery":      { lp: 600, q: 0.4, gain: 0.0032 },
  "product-focus":         { lp: 700, q: 0.5, gain: 0.0040 },
  "manufacturer-kdln":     { lp: 720, q: 0.5, gain: 0.0036 },
  "manufacturer-quasar":   { lp: 880, q: 0.45, gain: 0.0034 },
  "manufacturer-vistosi":  { lp: 760, q: 0.5, gain: 0.0040 },
  "manufacturer-nemo":     { lp: 460, q: 0.4, gain: 0.0030 },
  "manufacturer-a-emotional": { lp: 380, q: 0.35, gain: 0.0034 },
  "manufacturer-vibieffe": { lp: 340, q: 0.35, gain: 0.0040 },
  "contact-silent":        { lp: 400, q: 0.4, gain: 0 },
};

let currentScene: SoundScene | null = null;
type ScenePartial = {
  osc: OscillatorNode;
  gain: GainNode;
  pan: StereoPannerNode;
  lfo?: OscillatorNode;
  lfoGain?: GainNode;
};
let scenePartial: ScenePartial | null = null;

function clearScenePartial() {
  if (!scenePartial || !ctx) return;
  const { osc, gain, pan, lfo, lfoGain } = scenePartial;
  const t = ctx.currentTime;
  try {
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(Math.max(gain.gain.value, 0.0001), t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.0);
    osc.stop(t + 1.2);
    if (lfo) {
      try { lfo.stop(t + 1.2); } catch {}
    }
    osc.onended = () => {
      try { osc.disconnect(); gain.disconnect(); pan.disconnect(); } catch {}
      try { lfo?.disconnect(); lfoGain?.disconnect(); } catch {}
    };
  } catch { /* */ }
  scenePartial = null;
}

/* ── Public API ──────────────────────────────────────────────────────── */

export const audioBus = {
  isEnabled: readEnabled,
  getMode: readMode,

  setEnabled(on: boolean) {
    writeEnabled(on);
    listeners.forEach((fn) => fn(on));
    if (on) {
      const c = ensureCtx();
      if (c?.state === "suspended") c.resume().catch(() => {});
    } else {
      this.stopAmbient();
    }
  },

  /** Set sound mode: off | subtle | immersive. Persists in localStorage. */
  setMode(mode: SoundMode) {
    writeMode(mode);
    if (mode === "off") {
      writeEnabled(false);
      listeners.forEach((fn) => fn(false));
      this.stopAmbient();
    } else {
      writeEnabled(true);
      listeners.forEach((fn) => fn(true));
      const c = ensureCtx();
      if (c?.state === "suspended") c.resume().catch(() => {});
      // If immersive and a scene is active, restart ambient at full character.
      if (mode === "subtle") this.stopAmbient();
    }
  },

  subscribe(fn: (enabled: boolean) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** True once the user has performed any real gesture (pointerdown/keydown/touchstart). */
  isUnlocked(): boolean { return unlocked; },

  /** Fires once when the global unlock happens. Returns an unsubscribe fn. */
  subscribeUnlock(fn: () => void): () => void {
    if (unlocked) { try { fn(); } catch {} return () => {}; }
    unlockListeners.add(fn);
    return () => unlockListeners.delete(fn);
  },

  async play(name: SoundName, opts?: { throttleMs?: number; force?: boolean; x?: number; y?: number }) {
    if (!opts?.force && !readEnabled()) return;
    if (!unlocked) return; // never play before first real gesture
    // Suppress hover cues entirely in "subtle" mode and on coarse pointers.
    const mode = readMode();
    if (mode === "off") return;
    if ((name === "hover" || name === "nav-hover") && (mode !== "immersive" || !isFinePointer())) return;

    const now = performance.now();
    if (now - lastAnyPlay < GLOBAL_THROTTLE_MS) return;
    const last = lastPlayed.get(name) ?? 0;
    const throttle = opts?.throttleMs ?? PER_CUE_THROTTLE_MS[name] ?? 150;
    if (now - last < throttle) return;
    lastAnyPlay = now;
    lastPlayed.set(name, now);

    const c = ensureCtx();
    if (!c) return;
    if (c.state === "suspended") { try { await c.resume(); } catch { /* */ } }
    const x = opts?.x ?? cursorX;
    const y = opts?.y ?? cursorY;
    withSpatial(x, y, () => { try { RECIPES[name]?.(); } catch {} });
  },

  /** Back-compat: spatial cue from explicit x. Now equivalent to play({x}). */
  playSpatial(name: SoundName, x: number) {
    return this.play(name, { x, y: cursorY });
  },

  /** Wheel-picker cues. Bypass global throttle for rapid detent ticks. */
  playWheel(cue: WheelCue, opts?: WheelOpts) {
    if (!readEnabled() || !unlocked) return;
    if (readMode() === "off") return;
    if (typeof document !== "undefined" && document.hidden) return;
    const t = performance.now();
    if (t - lastWheelAt[cue] < WHEEL_THROTTLE_MS[cue]) return;
    lastWheelAt[cue] = t;
    const c = ensureCtx();
    if (!c) return;
    if (c.state === "suspended") { c.resume().catch(() => {}); }
    withSpatial(cursorX, cursorY, () => { try { wheelRecipes[cue](opts); } catch {} });
  },

  /** Start an ambient scene (per-page room tone). Idempotent on same scene. */
  startScene(scene: SoundScene) {
    if (!readEnabled() || !unlocked || reducedMotion()) return;
    if (readMode() !== "immersive") return; // ambient only in immersive
    if (currentScene === scene && ambient) return;
    if (currentScene !== scene) this.stopAmbient();
    currentScene = scene;
    const profile = SCENE_PROFILES[scene];
    if (!profile || profile.gain <= 0) return;
    const c = ensureCtx();
    if (!c || !master) return;
    const buf = ensurePink();
    if (!buf) return;
    // Room-tone bed
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = profile.lp;
    filter.Q.value = profile.q;
    const gain = c.createGain();
    gain.gain.value = 0.0001;
    src.connect(filter).connect(gain).connect(master);
    src.start();
    gain.gain.exponentialRampToValueAtTime(profile.gain, c.currentTime + 3.2);
    ambient = { src, gain, filter };
    // No tonal partial / no LFO oscillator — scenes are pure non-tonal air.
  },

  /** Cross-fade to a new scene. */
  transitionScene(scene: SoundScene) {
    if (currentScene === scene) return;
    this.stopAmbient();
    // Slight delay so the fade-out can begin before the new scene starts.
    setTimeout(() => this.startScene(scene), 350);
  },

  /** Legacy alias — use startScene("neutral") for the original behavior. */
  startAmbient() { this.startScene("neutral"); },

  stopAmbient() {
    currentScene = null;
    clearScenePartial();
    if (!ambient || !ctx) return;
    const { src, gain, filter } = ambient;
    const t = ctx.currentTime;
    try {
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(Math.max(gain.gain.value, 0.0001), t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
      src.stop(t + 1.4);
      src.onended = () => { try { src.disconnect(); gain.disconnect(); filter.disconnect(); } catch {} };
    } catch { /* */ }
    ambient = null;
  },

  /* ── Spatial card layer ────────────────────────────────────────────
   * Subtle cursor-driven card "trace" — one quiet voice per hovered card.
   * Cursor X → StereoPannerNode pan (tight ±0.35), cursor Y → filter
   * cutoff, distance from center → presence. One node per card; nothing
   * created on mousemove. Showroom mode only.
   */
  startCardSpatial(id: string, pos: SpatialPos) {
    if (!readEnabled() || !unlocked) return;
    if (readMode() !== "immersive") return;
    if (!isFinePointer()) return;
    if (reducedMotion()) return;
    if (typeof document !== "undefined" && document.hidden) return;
    if (spatialCards.has(id)) { this.updateCardSpatial(id, pos); return; }
    const c = ensureCtx();
    if (!c || !master) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    const buf = ensurePink();
    if (!buf) return;
    const t = c.currentTime;

    // Two filtered noise voices share the panner. No oscillator/sine layer:
    //   • body  = warm low-mid material air (felt/brushed metal body)
    //   • air   = high airy glass/material shimmer (texture, not tone)
    // Cursor X → pan, Cursor Y → body lowpass cutoff (warmth/brightness),
    // proximity → presence gain, velocity → tiny air boost.
    const pan = c.createStereoPanner();
    pan.pan.value = clamp((pos.x - 0.5) * 0.9, -0.45, 0.45);
    pan.connect(master);

    // Body voice — low-mid filtered air.
    const bodySrc = c.createBufferSource();
    bodySrc.buffer = buf; bodySrc.loop = true;
    const bodyHp = c.createBiquadFilter(); bodyHp.type = "highpass"; bodyHp.frequency.value = 180; bodyHp.Q.value = 0.4;
    const lp = c.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1600; lp.Q.value = 0.5;
    const noiseGain = c.createGain(); noiseGain.gain.value = 0.0001;
    bodySrc.connect(bodyHp).connect(lp).connect(noiseGain).connect(pan);
    bodySrc.start(t, Math.random() * (buf.duration - 1));

    // Air voice — high glass/material shimmer (filtered noise, NOT a sine).
    const airSrc = c.createBufferSource();
    airSrc.buffer = buf; airSrc.loop = true;
    const airHp = c.createBiquadFilter(); airHp.type = "highpass"; airHp.frequency.value = 4200; airHp.Q.value = 0.5;
    const airLp = c.createBiquadFilter(); airLp.type = "lowpass"; airLp.frequency.value = 8800; airLp.Q.value = 0.4;
    const airGain = c.createGain(); airGain.gain.value = 0.0001;
    airSrc.connect(airHp).connect(airLp).connect(airGain).connect(pan);
    airSrc.start(t, Math.random() * (buf.duration - 1));

    // Soft enter envelope. Absolute peaks stay ≤ 0.008 / 0.003 (per spec).
    noiseGain.gain.exponentialRampToValueAtTime(0.0060, t + 0.14);
    airGain.gain.exponentialRampToValueAtTime(0.0022, t + 0.20);

    spatialCards.set(id, { bodySrc, airSrc, noiseGain, airGain, lp, airLp, pan });
    this.updateCardSpatial(id, pos);
  },

  updateCardSpatial(id: string, pos: SpatialPos) {
    const card = spatialCards.get(id);
    if (!card || !ctx) return;
    const t = ctx.currentTime;
    const targetPan = clamp((pos.x - 0.5) * 0.9, -0.45, 0.45);
    // Vertical brightness: top → ~3000Hz body cutoff, bottom → ~1200Hz (warmer)
    const bodyCutoff = 1200 + (1 - clamp(pos.y, 0, 1)) * 1800;
    // Air brightness — top of card opens the air band a touch.
    const airCutoff = 7200 + (1 - clamp(pos.y, 0, 1)) * 2400;
    // Proximity → presence
    const presence = 1 - clamp(pos.dist, 0, 1) * 0.65;
    // Velocity → tiny air texture boost
    const vel = clamp(pos.velocity ?? 0, 0, 1);

    try {
      card.pan.pan.cancelScheduledValues(t);
      card.pan.pan.linearRampToValueAtTime(targetPan, t + 0.08);
      card.lp.frequency.cancelScheduledValues(t);
      card.lp.frequency.linearRampToValueAtTime(bodyCutoff, t + 0.10);
      card.airLp.frequency.cancelScheduledValues(t);
      card.airLp.frequency.linearRampToValueAtTime(airCutoff, t + 0.10);
      card.noiseGain.gain.cancelScheduledValues(t);
      card.noiseGain.gain.linearRampToValueAtTime(Math.max(0.0001, 0.0055 * presence + 0.0015 * vel), t + 0.10);
      card.airGain.gain.cancelScheduledValues(t);
      card.airGain.gain.linearRampToValueAtTime(Math.max(0.0001, 0.0022 * presence * (1 - clamp(pos.y, 0, 1) * 0.5) + 0.0010 * vel), t + 0.10);
    } catch { /* */ }
  },

  stopCardSpatial(id: string) {
    const card = spatialCards.get(id);
    if (!card || !ctx) return;
    spatialCards.delete(id);
    const t = ctx.currentTime;
    try {
      [card.noiseGain, card.airGain].forEach((g) => {
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), t);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
      });
      card.bodySrc.stop(t + 0.36);
      card.airSrc.stop(t + 0.36);
      card.bodySrc.onended = () => {
        try {
          card.bodySrc.disconnect(); card.lp.disconnect();
          card.airSrc.disconnect(); card.airLp.disconnect();
          card.noiseGain.disconnect(); card.airGain.disconnect();
          card.pan.disconnect();
        } catch {}
      };
    } catch { /* */ }
  },

  cleanupAllSpatialCards() {
    Array.from(spatialCards.keys()).forEach((id) => this.stopCardSpatial(id));
  },
};

type SpatialPos = { x: number; y: number; dist: number; velocity?: number };
type SpatialCard = {
  bodySrc: AudioBufferSourceNode;
  airSrc: AudioBufferSourceNode;
  noiseGain: GainNode;
  airGain: GainNode;
  lp: BiquadFilterNode;
  airLp: BiquadFilterNode;
  pan: StereoPannerNode;
};
const spatialCards = new Map<string, SpatialCard>();
function clamp(v: number, lo: number, hi: number) { return v < lo ? lo : v > hi ? hi : v; }

if (isBrowser) {
  // Cleanup ALL persistent audio when the tab is hidden or window blurs.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      audioBus.cleanupAllSpatialCards();
      audioBus.stopAmbient();
    }
  });
  window.addEventListener("blur", () => {
    audioBus.cleanupAllSpatialCards();
    audioBus.stopAmbient();
  });
}

/* ── First-gesture unlock + global delegation ────────────────────────── */

let unlocked = false;
const unlockListeners = new Set<() => void>();
function unlockOnce() {
  if (unlocked || !isBrowser) return;
  unlocked = true;
  const c = ensureCtx();
  if (c?.state === "suspended") c.resume().catch(() => {});
  unlockListeners.forEach((fn) => { try { fn(); } catch {} });
  unlockListeners.clear();
}

const VALID = new Set<SoundName>([
  "hover","nav-hover","tap","click","open","close","transition","reveal","chime","success","error",
]);

const ALIASES: Record<string, SoundName> = {
  // Product / lightbox / menu reveal & close
  "product-open":     "open",
  "product-close":    "close",
  "lightbox-open":    "open",
  "lightbox-close":   "close",
  "menu-open":        "open",
  "menu-close":       "close",
  "image-zoom-in":    "open",
  "image-zoom-out":   "close",
  // Premium dimmer family — tabs, filters, theme, dimmer-style toggles
  "theme-toggle":     "chime",
  "filter-toggle":    "chime",
  "category-change":  "chime",
  "image-next":       "chime",
  "image-prev":       "chime",
  "back":             "close",
  // Forms & confirmations
  "form-success":     "success",
  "form-error":       "error",
  "copy":             "tap",
  "external":         "transition",
  // Reveal / scroll family
  "section-reveal":   "reveal",
  "text-reveal":      "reveal",
  "fast-scroll":      "reveal",
  "scroll-settle":    "reveal",
  "route":            "transition",
  // Manufacturer brand cues — share the showroom reveal envelope; the
  // active scene profile (startScene) supplies the brand-specific air.
  "manufacturer-enter":         "open",
  "manufacturer-logo-reveal":   "reveal",
  "manufacturer-product-open":  "open",
  "manufacturer-product-hover": "hover",
};

function resolveCue(el: Element | null): { tap?: SoundName; hover?: SoundName; off?: boolean } {
  let cur: Element | null = el;
  while (cur && cur !== document.body) {
    const v = cur.getAttribute?.("data-sound");
    if (v) {
      if (v === "off") return { off: true };
      if (VALID.has(v as SoundName)) {
        if (v === "hover" || v === "nav-hover") return { hover: v as SoundName };
        return { tap: v as SoundName };
      }
      const aliased = ALIASES[v];
      if (aliased) return { tap: aliased };
    }
    cur = cur.parentElement;
  }
  return {};
}

function defaultTapFor(target: Element | null): SoundName | null {
  if (!target) return null;
  if (target.closest("[data-sound='off']")) return null;
  // Only confirm on real interactive controls. Plain links navigate and the
  // route-transition cue covers them — don't double-announce.
  if (target.closest("button:not([disabled]), [role='button']")) return "tap";
  return null;
}

function installDelegation() {
  if (!isBrowser) return;

  const unlock = () => {
    unlockOnce();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };
  window.addEventListener("pointerdown", unlock, { passive: true });
  window.addEventListener("keydown", unlock);
  window.addEventListener("touchstart", unlock, { passive: true });

  // Global cursor / touch tracker — feeds spatial pan + Y-filter for EVERY
  // cue (clicks, hovers, wheel detents, lightbox open/close, etc.).
  const trackXY = (x: number, y: number) => {
    const vw = window.innerWidth || 1;
    const vh = window.innerHeight || 1;
    cursorX = Math.max(0, Math.min(1, x / vw));
    cursorY = Math.max(0, Math.min(1, y / vh));
  };
  window.addEventListener("pointermove", (e) => trackXY(e.clientX, e.clientY), { passive: true });
  window.addEventListener("pointerdown", (e) => trackXY(e.clientX, e.clientY), { passive: true });
  window.addEventListener("touchstart", (e) => {
    const t = e.touches[0]; if (t) trackXY(t.clientX, t.clientY);
  }, { passive: true });
  window.addEventListener("touchmove", (e) => {
    const t = e.touches[0]; if (t) trackXY(t.clientX, t.clientY);
  }, { passive: true });

  // Hover — desktop only, opt-in only. Tracks last hovered element so we don't
  // re-fire on every pixel of mouse movement inside the same target.
  let lastHover: Element | null = null;
  if (isFinePointer()) {
    window.addEventListener("pointerover", (e) => {
      if (!readEnabled()) return;
      if ((e as PointerEvent).pointerType !== "mouse") return;
      const target = e.target as Element | null;
      if (!target) return;
      const cue = resolveCue(target);
      if (cue.off || !cue.hover) return;
      // Find the actual opt-in container so we de-dupe correctly.
      const owner = target.closest("[data-sound]");
      if (owner === lastHover) return;
      lastHover = owner;
      audioBus.play(cue.hover);
    }, { passive: true });
    window.addEventListener("pointerout", (e) => {
      const related = (e as PointerEvent).relatedTarget as Element | null;
      if (!related || !related.closest?.("[data-sound]")) lastHover = null;
    }, { passive: true });
  }

  // Tap — opt-in (data-sound) + sensible default for buttons.
  window.addEventListener("pointerup", (e) => {
    if (!readEnabled()) return;
    const target = e.target as Element | null;
    if (!target) return;
    const cue = resolveCue(target);
    if (cue.off) return;
    const name = cue.tap ?? defaultTapFor(target);
    if (name) audioBus.play(name);
  }, { passive: true });
}

if (isBrowser) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", installDelegation, { once: true });
  } else {
    installDelegation();
  }
}

/** Light haptic pulse for important taps. No-op where unsupported. */
export function hapticTap(intensity: "light" | "medium" = "light") {
  if (!isBrowser) return;
  if (reducedMotion()) return;
  if (!("vibrate" in navigator)) return;
  try { navigator.vibrate(intensity === "medium" ? 12 : 6); } catch { /* */ }
}
